import React, { useState } from 'react';
import { EnvelopeIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';
import EmailService, { NotificationTypes } from '../../services/emailService';

const EmailSubscription = ({ className = '', showTitle = true, compact = false }) => {
  const [email, setEmail] = useState('');
  const [preferences, setPreferences] = useState({
    [NotificationTypes.EVENTS]: true,
    [NotificationTypes.NEWS]: true,
    [NotificationTypes.NEWSLETTERS]: true,
    [NotificationTypes.REMINDERS]: false
  });
  const [isSubscribing, setIsSubscribing] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    
    if (!email) {
      toast.error('Please enter your email address');
      return;
    }

    if (!email.includes('@') || !email.includes('.')) {
      toast.error('Please enter a valid email address');
      return;
    }

    try {
      setIsSubscribing(true);
      await EmailService.subscribeToEmails(email, preferences);
      setIsSubscribed(true);
      toast.success('Successfully subscribed to email notifications!');
      
      // Reset form after successful subscription
      setTimeout(() => {
        setEmail('');
        setIsSubscribed(false);
      }, 3000);
      
    } catch (error) {
      console.error('Subscription error:', error);
      toast.error('Failed to subscribe. Please try again.');
    } finally {
      setIsSubscribing(false);
    }
  };

  const handlePreferenceChange = (type) => {
    setPreferences(prev => ({
      ...prev,
      [type]: !prev[type]
    }));
  };

  if (isSubscribed) {
    return (
      <div className={`bg-green-50 border border-green-200 rounded-lg p-6 text-center ${className}`}>
        <CheckCircleIcon className="h-12 w-12 text-green-500 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-green-800 mb-2">
          Subscription Confirmed!
        </h3>
        <p className="text-green-700">
          You'll receive email notifications based on your preferences.
        </p>
      </div>
    );
  }

  if (compact) {
    return (
      <div className={`bg-white rounded-lg shadow-sm border p-4 ${className}`}>
        <form onSubmit={handleSubscribe} className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email address"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              disabled={isSubscribing}
            />
          </div>
          <button
            type="submit"
            disabled={isSubscribing}
            className="btn-primary whitespace-nowrap"
          >
            {isSubscribing ? 'Subscribing...' : 'Subscribe'}
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className={`bg-white rounded-lg shadow-sm border p-6 ${className}`}>
      {showTitle && (
        <div className="text-center mb-6">
          <EnvelopeIcon className="h-12 w-12 text-primary-600 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-gray-900 mb-2">
            Stay Updated
          </h3>
          <p className="text-gray-600">
            Subscribe to receive email notifications about events, news, and important updates.
          </p>
        </div>
      )}

      <form onSubmit={handleSubscribe} className="space-y-4">
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
            Email Address
          </label>
          <input
            type="email"
            id="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Enter your email address"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            disabled={isSubscribing}
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3">
            Email Preferences
          </label>
          <div className="space-y-2">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={preferences[NotificationTypes.EVENTS]}
                onChange={() => handlePreferenceChange(NotificationTypes.EVENTS)}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
              <span className="ml-2 text-sm text-gray-700">
                Event notifications (games, practices, schedule changes)
              </span>
            </label>
            
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={preferences[NotificationTypes.NEWS]}
                onChange={() => handlePreferenceChange(NotificationTypes.NEWS)}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
              <span className="ml-2 text-sm text-gray-700">
                News and announcements
              </span>
            </label>
            
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={preferences[NotificationTypes.NEWSLETTERS]}
                onChange={() => handlePreferenceChange(NotificationTypes.NEWSLETTERS)}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
              <span className="ml-2 text-sm text-gray-700">
                Monthly newsletters
              </span>
            </label>
            
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={preferences[NotificationTypes.REMINDERS]}
                onChange={() => handlePreferenceChange(NotificationTypes.REMINDERS)}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
              <span className="ml-2 text-sm text-gray-700">
                Registration and deadline reminders
              </span>
            </label>
          </div>
        </div>

        <button
          type="submit"
          disabled={isSubscribing || Object.values(preferences).every(pref => !pref)}
          className="w-full btn-primary flex items-center justify-center"
        >
          <EnvelopeIcon className="h-4 w-4 mr-2" />
          {isSubscribing ? 'Subscribing...' : 'Subscribe to Notifications'}
        </button>
      </form>

      <p className="text-xs text-gray-500 text-center mt-4">
        You can unsubscribe at any time by clicking the unsubscribe link in any email.
      </p>
    </div>
  );
};

export default EmailSubscription;